import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useIsLargeScreen } from '../../hooks/useIsLargeScreen';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { WorkerProfileModal } from '../../components/shared/WorkerProfileModal';
import { 
  Star, ShieldCheck, Award, UserPlus, UserMinus, 
  UserCheck, Loader2, Search, X, AlertTriangle,
  ChevronRight, Clock, ChevronDown, CalendarDays,
  Send, Trash2, MailPlus, CheckCircle2, ArrowLeft, RotateCcw, History
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function StaffPage() {
  const { api, token } = useAuth();
  const isLargeScreen = useIsLargeScreen();
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [myStaff, setMyStaff] = useState([]);
  const [historyStaff, setHistoryStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Remove confirmation modal
  const [removeModal, setRemoveModal] = useState({ open: false, worker: null });

  // Profile modal state
  const [selectedWorkerId, setSelectedWorkerId] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [workersRes, staffRes, historyRes] = await Promise.all([
        api.get('/users/workers'),
        api.get('/staff/my-staff'),
        api.get('/staff/history'),
      ]);
      setWorkers(workersRes.data);
      setMyStaff(staffRes.data);
      setHistoryStaff(historyRes.data);
    } catch {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openWorkerProfile = async (worker) => {
    setSelectedWorkerId(worker.id);
    setProfileLoading(true);
    try {
      const res = await api.get(`/staff/worker/${worker.id}/profile`);
      setProfileData(res.data);
    } catch {
      toast.error('Failed to load worker profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleAddStaff = async () => {
    if (!addEmail.trim()) return;
    setAdding(true);
    try {
      const res = await api.post('/staff/add', { email: addEmail });
      toast.success(res.data.message);
      setAddEmail('');
      setShowAddDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add worker');
    } finally {
      setAdding(false);
    }
  };

  const handleResendInvite = async (linkId, e) => {
    e?.stopPropagation();
    setActionLoading('resend-' + linkId);
    try {
      await api.post(`/staff/${linkId}/resend-invite`);
      toast.success('Invitation resent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemovePending = async (linkId, e) => {
    e?.stopPropagation();
    setActionLoading('remove-' + linkId);
    try {
      await api.delete(`/staff/${linkId}`);
      toast.success('Invitation removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    } finally {
      setActionLoading(null);
    }
  };

  const confirmRemoveFromActive = async () => {
    const worker = removeModal.worker;
    if (!worker) return;
    setActionLoading('remove-active-' + worker.staffLinkId);
    try {
      await api.post(`/staff/${worker.staffLinkId}/remove`);
      toast.success(`${worker.fullName} moved to Staff History`);
      setRemoveModal({ open: false, worker: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestore = async (linkId, e) => {
    e?.stopPropagation();
    setActionLoading('restore-' + linkId);
    try {
      await api.post(`/staff/${linkId}/restore`);
      toast.success('Worker restored to Active Staff');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveFromProfile = async (linkId, worker) => {
    setActionLoading('remove-active-' + linkId);
    try {
      await api.post(`/staff/${linkId}/remove`);
      toast.success('Worker removed from your list.');
      setSelectedWorkerId(null);
      setProfileData(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove worker');
    } finally {
      setActionLoading(null);
    }
  };

  const handleVerifyToggle = async (workerId) => {
    setActionLoading('verify-' + workerId);
    try {
      await api.patch(`/users/${workerId}/verify-smart-id`);
      toast.success('Verification updated');
      const res = await api.get(`/staff/worker/${workerId}/profile`);
      setProfileData(res.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update verification');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivate = async (linkId, e) => {
    e?.stopPropagation();
    // For the profile modal, use the remove flow instead
    const worker = myStaff.find(w => w.staffLinkId === linkId);
    if (worker) {
      setRemoveModal({ open: true, worker });
    }
  };

  const handleReactivate = async (linkId, e) => {
    e?.stopPropagation();
    await handleRestore(linkId, e);
  };

  const getReliabilityColor = (score) => {
    if (score >= 85) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const formatDate = (str) => {
    if (!str) return '-';
    return new Date(str).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getPhotoUrl = (path) => {
    if (!path) return null;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    return `${API_URL}/api/files/${path}?auth=${token}`;
  };

  // History grouped by month
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [historyInit, setHistoryInit] = useState(false);

  const historyGrouped = useMemo(() => {
    const map = new Map();
    for (const w of historyStaff) {
      const dateStr = w.removedAt || '';
      const key = dateStr ? dateStr.slice(0, 7) : 'unknown';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(w);
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [historyStaff]);

  // Auto-expand first month
  if (!historyInit && historyGrouped.length > 0 && tab === 'history') {
    setExpandedMonths(new Set([historyGrouped[0][0]]));
    setHistoryInit(true);
  }

  const toggleMonth = (key) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const formatMonthLabel = (key) => {
    if (key === 'unknown') return 'Unknown';
    const [y, m] = key.split('-');
    return format(new Date(parseInt(y), parseInt(m) - 1, 1), 'MMMM yyyy');
  };

  // Display data based on tab
  const displayWorkers = tab === 'active' ? myStaff : tab === 'all' ? workers : [];
  const filteredWorkers = displayWorkers.filter(w => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return w.fullName?.toLowerCase().includes(q) || w.email?.toLowerCase().includes(q);
  });

  // Search in history too
  const filteredHistory = useMemo(() => {
    if (!searchQuery || tab !== 'history') return historyGrouped;
    const q = searchQuery.toLowerCase();
    return historyGrouped
      .map(([key, items]) => [key, items.filter(w => w.fullName?.toLowerCase().includes(q) || w.email?.toLowerCase().includes(q))])
      .filter(([, items]) => items.length > 0);
  }, [historyGrouped, searchQuery, tab]);

  const PendingCard = ({ worker }) => (
    <Card className="border-0 shadow-sm border-l-4 border-l-amber-400" data-testid={`pending-invite-${worker.staffLinkId}`}>
      <CardContent className="p-5">
        <div className="flex items-start gap-4 mb-3">
          <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 text-amber-600">
            <MailPlus className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <Badge className="bg-amber-100 text-amber-800 gap-1 text-xs mb-1"><Clock className="h-3 w-3" /> Pending Invite</Badge>
            <p className="text-sm text-slate-700 font-medium truncate">{worker.email}</p>
            {worker.invitedAt && <p className="text-xs text-slate-400 mt-0.5">Invited {formatDate(worker.invitedAt)}</p>}
          </div>
        </div>
        <div className="flex gap-2 pt-3 border-t border-slate-100">
          <Button size="sm" variant="outline" className="flex-1 rounded-full text-blue-600 border-blue-200 hover:bg-blue-50 text-xs h-8"
            onClick={(e) => handleResendInvite(worker.staffLinkId, e)}
            disabled={actionLoading === 'resend-' + worker.staffLinkId}
            data-testid={`resend-invite-${worker.staffLinkId}`}
          >
            {actionLoading === 'resend-' + worker.staffLinkId ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
            Resend
          </Button>
          <Button size="sm" variant="outline" className="flex-1 rounded-full text-red-600 border-red-200 hover:bg-red-50 text-xs h-8"
            onClick={(e) => handleRemovePending(worker.staffLinkId, e)}
            disabled={actionLoading === 'remove-' + worker.staffLinkId}
            data-testid={`remove-pending-${worker.staffLinkId}`}
          >
            {actionLoading === 'remove-' + worker.staffLinkId ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Trash2 className="h-3.5 w-3.5 mr-1" />}
            Remove
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const ActiveWorkerCard = ({ worker }) => (
    <Card 
      className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer group"
      onClick={() => openWorkerProfile(worker)}
      data-testid={`worker-card-${worker.id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4 mb-3">
          {worker.profilePhoto ? (
            <img src={getPhotoUrl(worker.profilePhoto)} alt={worker.fullName} className="h-12 w-12 rounded-full object-cover flex-shrink-0 group-hover:scale-105 transition-transform" />
          ) : (
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#0f1b3d] to-[#2a3f6d] flex items-center justify-center flex-shrink-0 text-white font-bold text-lg group-hover:scale-105 transition-transform">
              {worker.fullName?.charAt(0)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-900 truncate">{worker.fullName}</h3>
              <Badge className="bg-emerald-100 text-emerald-800 text-xs gap-1"><CheckCircle2 className="h-3 w-3" /> Active</Badge>
            </div>
            <p className="text-sm text-slate-500 truncate">{worker.email}</p>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-slate-500 transition-colors shrink-0 mt-1" />
        </div>

        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {worker.smartIdVerified ? (
            <Badge className="bg-emerald-100 text-emerald-800 gap-1 text-xs"><ShieldCheck className="h-3 w-3" /> Verified</Badge>
          ) : (
            <Badge className="bg-slate-100 text-slate-600 text-xs">Unverified</Badge>
          )}
          {worker.ratingAverage >= 4.5 && worker.completedShiftsCount >= 10 && (
            <Badge className="bg-amber-100 text-amber-800 gap-1 text-xs"><Award className="h-3 w-3" /> Top Rated</Badge>
          )}
          {(worker.reliabilityScore || 100) < 60 && (
            <Badge className="bg-red-100 text-red-800 gap-1 text-xs"><AlertTriangle className="h-3 w-3" /> At Risk</Badge>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 py-2.5 px-3 bg-slate-50 rounded-lg text-center">
          <div>
            <div className="flex items-center justify-center gap-1 text-amber-500">
              <Star className="h-3.5 w-3.5 fill-current" />
              <span className="font-semibold text-sm text-slate-900">{(worker.ratingAverage || 0).toFixed(1)}</span>
            </div>
            <p className="text-[11px] text-slate-500">Rating</p>
          </div>
          <div>
            <span className={`font-semibold text-sm ${getReliabilityColor(worker.reliabilityScore || 100)}`}>
              {(worker.reliabilityScore || 100).toFixed(0)}%
            </span>
            <p className="text-[11px] text-slate-500">Reliable</p>
          </div>
          <div>
            <span className="font-semibold text-sm text-slate-900">{worker.completedShiftsCount || 0}</span>
            <p className="text-[11px] text-slate-500">Shifts</p>
          </div>
        </div>

        <div className="pt-3 mt-3 border-t border-slate-100">
          <Button size="sm" variant="outline" className="w-full rounded-full text-red-600 border-red-200 hover:bg-red-50 text-xs h-8"
            onClick={(e) => { e.stopPropagation(); setRemoveModal({ open: true, worker }); }}
            disabled={actionLoading === 'remove-active-' + worker.staffLinkId}
            data-testid={`remove-staff-${worker.id}`}
          >
            {actionLoading === 'remove-active-' + worker.staffLinkId ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <UserMinus className="h-3.5 w-3.5 mr-1.5" />}
            Remove from Active Staff
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const HistoryWorkerCard = ({ worker }) => (
    <Card 
      className="border-0 shadow-sm bg-slate-50/80 hover:bg-white transition-all cursor-pointer group"
      onClick={() => openWorkerProfile(worker)}
      data-testid={`history-card-${worker.id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4 mb-3">
          {worker.profilePhoto ? (
            <img src={getPhotoUrl(worker.profilePhoto)} alt={worker.fullName} className="h-12 w-12 rounded-full object-cover flex-shrink-0 opacity-80" />
          ) : (
            <div className="h-12 w-12 rounded-full bg-slate-300 flex items-center justify-center flex-shrink-0 text-white font-bold text-lg">
              {worker.fullName?.charAt(0)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-700 truncate">{worker.fullName}</h3>
              <Badge className="bg-slate-200 text-slate-600 text-xs gap-1">Removed</Badge>
            </div>
            <p className="text-sm text-slate-400 truncate">{worker.email}</p>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-slate-500 transition-colors shrink-0 mt-1" />
        </div>

        <div className="grid grid-cols-3 gap-2 py-2 px-3 bg-white/50 rounded-lg text-center mb-3">
          <div>
            <div className="flex items-center justify-center gap-1 text-amber-400">
              <Star className="h-3 w-3 fill-current" />
              <span className="font-semibold text-xs text-slate-600">{(worker.ratingAverage || 0).toFixed(1)}</span>
            </div>
            <p className="text-[10px] text-slate-400">Rating</p>
          </div>
          <div>
            <span className={`font-semibold text-xs ${getReliabilityColor(worker.reliabilityScore || 100)}`}>
              {(worker.reliabilityScore || 100).toFixed(0)}%
            </span>
            <p className="text-[10px] text-slate-400">Reliable</p>
          </div>
          <div>
            <span className="font-semibold text-xs text-slate-600">{worker.completedShiftsCount || 0}</span>
            <p className="text-[10px] text-slate-400">Shifts</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 text-xs text-slate-400 mb-3">
          {worker.lastWorkedDate && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" /> Last worked: {formatDate(worker.lastWorkedDate)}
            </span>
          )}
          {worker.removedAt && (
            <span className="flex items-center gap-1">
              <History className="h-3 w-3" /> Removed: {formatDate(worker.removedAt)}
            </span>
          )}
        </div>

        <Button size="sm" variant="outline" className="w-full rounded-full text-emerald-600 border-emerald-200 hover:bg-emerald-50 text-xs h-8"
          onClick={(e) => handleRestore(worker.staffLinkId, e)}
          disabled={actionLoading === 'restore-' + worker.staffLinkId}
          data-testid={`restore-staff-${worker.id}`}
        >
          {actionLoading === 'restore-' + worker.staffLinkId ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
          Restore to Active Staff
        </Button>
      </CardContent>
    </Card>
  );

  const AllWorkerCard = ({ worker }) => (
    <Card 
      className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer group"
      onClick={() => openWorkerProfile(worker)}
      data-testid={`worker-card-${worker.id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4 mb-3">
          {worker.profilePhoto ? (
            <img src={getPhotoUrl(worker.profilePhoto)} alt={worker.fullName} className="h-12 w-12 rounded-full object-cover flex-shrink-0 group-hover:scale-105 transition-transform" />
          ) : (
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#0f1b3d] to-[#2a3f6d] flex items-center justify-center flex-shrink-0 text-white font-bold text-lg group-hover:scale-105 transition-transform">
              {worker.fullName?.charAt(0)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 truncate">{worker.fullName}</h3>
            <p className="text-sm text-slate-500 truncate">{worker.email}</p>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-slate-500 transition-colors shrink-0 mt-1" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {worker.smartIdVerified ? (
            <Badge className="bg-emerald-100 text-emerald-800 gap-1 text-xs"><ShieldCheck className="h-3 w-3" /> Verified</Badge>
          ) : (
            <Badge className="bg-slate-100 text-slate-600 text-xs">Unverified</Badge>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2 py-2.5 px-3 bg-slate-50 rounded-lg text-center mt-3">
          <div>
            <div className="flex items-center justify-center gap-1 text-amber-500">
              <Star className="h-3.5 w-3.5 fill-current" />
              <span className="font-semibold text-sm text-slate-900">{(worker.ratingAverage || 0).toFixed(1)}</span>
            </div>
            <p className="text-[11px] text-slate-500">Rating</p>
          </div>
          <div>
            <span className={`font-semibold text-sm ${getReliabilityColor(worker.reliabilityScore || 100)}`}>
              {(worker.reliabilityScore || 100).toFixed(0)}%
            </span>
            <p className="text-[11px] text-slate-500">Reliable</p>
          </div>
          <div>
            <span className="font-semibold text-sm text-slate-900">{worker.completedShiftsCount || 0}</span>
            <p className="text-[11px] text-slate-500">Shifts</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-48" />)}</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="staff-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" className="rounded-full h-11 w-11 min-w-[44px] shrink-0" onClick={() => navigate('/manager')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']" data-walkthrough={!isLargeScreen ? 'wt-mgr-staff' : undefined}>Staff</h1>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">Manage your team and view worker history</p>
          </div>
        </div>
        <Button className="rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white h-11 shrink-0" onClick={() => setShowAddDialog(true)} data-testid="add-staff-btn">
          <UserPlus className="h-4 w-4 mr-2" /> Add Staff
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setSearchQuery(''); }}>
        <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto scrollbar-thin">
          <TabsList className="bg-slate-100 p-1 rounded-xl inline-flex w-auto min-w-full sm:w-full">
            <TabsTrigger value="active" className="rounded-lg whitespace-nowrap min-h-[44px]" data-testid="tab-active">
              Active ({myStaff.filter(w => !w.isPending).length})
            </TabsTrigger>
            <TabsTrigger value="history" className="rounded-lg whitespace-nowrap min-h-[44px]" data-testid="tab-history">
              History ({historyStaff.length})
            </TabsTrigger>
            <TabsTrigger value="all" className="rounded-lg whitespace-nowrap min-h-[44px]" data-testid="tab-all">
              All ({workers.length})
            </TabsTrigger>
          </TabsList>
        </div>
      </Tabs>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input placeholder="Search by name or email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          className="pl-10 h-11 rounded-xl bg-slate-50 border-slate-200" data-testid="staff-search-input" />
        {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="h-4 w-4 text-slate-400" /></button>}
      </div>

      {/* Active Staff Tab */}
      {tab === 'active' && (
        filteredWorkers.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <UserCheck className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No active staff members. Add workers to your team.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredWorkers.map(worker => 
              worker.isPending 
                ? <PendingCard key={worker.staffLinkId} worker={worker} />
                : <ActiveWorkerCard key={worker.staffLinkId || worker.id} worker={worker} />
            )}
          </div>
        )
      )}

      {/* Staff History Tab */}
      {tab === 'history' && (
        historyStaff.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <History className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No staff history yet. Removed staff will appear here.</p>
            </CardContent>
          </Card>
        ) : filteredHistory.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <Search className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No matching staff in history.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="staff-history-grouped">
            {filteredHistory.map(([monthKey, items]) => {
              const isOpen = expandedMonths.has(monthKey);
              return (
                <Collapsible key={monthKey} open={isOpen} onOpenChange={() => toggleMonth(monthKey)}>
                  <CollapsibleTrigger asChild>
                    <button
                      className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${isOpen ? 'bg-slate-100' : 'bg-slate-50 hover:bg-slate-100'}`}
                      data-testid={`history-month-${monthKey}`}
                    >
                      <div className="flex items-center gap-3">
                        <CalendarDays className="h-4 w-4 text-slate-400" />
                        <span className="font-semibold text-slate-900 text-sm">{formatMonthLabel(monthKey)}</span>
                        <Badge className="bg-slate-200 text-slate-600 text-xs">{items.length}</Badge>
                      </div>
                      <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mt-3">
                      {items.map(worker => <HistoryWorkerCard key={worker.staffLinkId || worker.id} worker={worker} />)}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
        )
      )}

      {/* All Workers Tab */}
      {tab === 'all' && (
        filteredWorkers.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <p className="text-slate-500">No workers found.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredWorkers.map(worker => <AllWorkerCard key={worker.id} worker={worker} />)}
          </div>
        )
      )}

      {/* Remove Confirmation Modal */}
      <Dialog open={removeModal.open} onOpenChange={(open) => { if (!open) setRemoveModal({ open: false, worker: null }); }}>
        <DialogContent className="sm:max-w-md" data-testid="remove-staff-modal">
          <DialogHeader>
            <DialogTitle>Remove from Active Staff</DialogTitle>
            <DialogDescription>
              This worker will be moved to Staff History. All data will be preserved.
            </DialogDescription>
          </DialogHeader>
          {removeModal.worker && (
            <div className="py-3">
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#0f1b3d] to-[#2a3f6d] flex items-center justify-center text-white font-bold text-lg">
                  {removeModal.worker.fullName?.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{removeModal.worker.fullName}</p>
                  <p className="text-sm text-slate-500">{removeModal.worker.email}</p>
                </div>
              </div>
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <p>This worker will be removed from your active staff list. You can restore them later from Staff History.</p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="rounded-full" onClick={() => setRemoveModal({ open: false, worker: null })}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="rounded-full"
              onClick={confirmRemoveFromActive}
              disabled={actionLoading !== null}
              data-testid="confirm-remove-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserMinus className="h-4 w-4 mr-2" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Staff Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Worker to Staff</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-500">Enter a worker's email. If they already have an account, they'll be linked immediately. If not, they'll receive an invitation and be linked when they sign up.</p>
            <div className="space-y-2">
              <Label>Worker Email</Label>
              <Input type="email" placeholder="worker@email.com" value={addEmail} onChange={e => setAddEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddStaff()}
                className="h-12 rounded-xl" data-testid="add-staff-email-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handleAddStaff} disabled={adding || !addEmail.trim()} className="rounded-full bg-[#0f1b3d] hover:bg-[#1a2d5a]" data-testid="confirm-add-staff-btn">
              {adding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />} Add to Staff
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Worker Profile Modal */}
      <WorkerProfileModal
        open={!!selectedWorkerId}
        onClose={() => { setSelectedWorkerId(null); setProfileData(null); }}
        profileData={profileData}
        profileLoading={profileLoading}
        onVerify={handleVerifyToggle}
        onDeactivate={handleDeactivate}
        onReactivate={handleReactivate}
        onRemove={handleRemoveFromProfile}
        actionLoading={actionLoading}
        showStaffActions={true}
        api={api}
        token={token}
      />
    </div>
  );
}
